/**
 * Cookie management utilities
 * Zero dependencies, type-safe cookie operations
 */

export interface CookieOptions {
  readonly expires?: number; // Days until expiration
  readonly path?: string;
  readonly domain?: string | undefined; // undefined = browser default (current domain)
  readonly secure?: boolean;
  readonly sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Set a cookie with optional configuration
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') {
    return; // SSR safety (though this is CSR-only project)
  }

  const {
    expires = 7, // Default: 7 days
    path,
    domain,
    secure = true,
    sameSite = 'Lax',
  } = options;

  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + expires * 24 * 60 * 60 * 1000);

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookieString += `; expires=${expirationDate.toUTCString()}`;

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  cookieString += `; samesite=${sameSite}`;

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name.
 * Handles both URL-encoded values (set by our setCookie) and
 * raw values (set by MMT's server via Set-Cookie header).
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      const value = cookie.substring(nameEQ.length);
      try {
        return decodeURIComponent(value);
      } catch {
        // Server-set cookies may not be URL-encoded; return raw value
        return value;
      }
    }
  }

  return null;
}

/**
 * Remove a cookie by name
 * Note: To properly delete a cookie, match the same path, domain, secure, and sameSite used when setting it
 */
export function removeCookie(
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain' | 'secure' | 'sameSite'> = {},
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const { path = '/', domain, secure, sameSite = 'Lax' } = options;

  let cookieString = `${encodeURIComponent(name)}=`;
  cookieString += '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  cookieString += `; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  cookieString += `; samesite=${sameSite}`;

  document.cookie = cookieString;
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}
